/**
 * SwiftFederation register form component
 * Supports: CDN, Transcoding, Transmuxing, Recording, StorageLFD, StorageOSS
 * Features: Entity name input + auto registration
 */
import useQueryClientCallback from '@/hooks/useQueryClientCallback'
import {
    registerService,
    updateServiceEntityName,
} from '@/modules/Service/services'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { GridHookForm } from 'vnetwork-ui'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { QUERY_KEYS, QUERY_KEY_COMBINATIONS } from '@/constants/reactQueryKeys'
import type { RegisterServiceTemplateProps } from '../types'

const SwiftFederationRegisterForm: React.FC<RegisterServiceTemplateProps> = ({
    service,
}) => {
    const { t } = useTranslation()
    const { refetch } = useQueryClientCallback({
        pattern: 'tickets',
        mode: 'contains',
    })
    const { refetch: refetchServices } = useQueryClientCallback({
        pattern: 'services',
        mode: 'contains',
    })

    const { mutateAsync: updateEntityName, isPending: isUpdating } =
        useMutationWithHandler(
            { mutationFn: updateServiceEntityName },
            {
                successMessage: t('update-service-entity-name-success'),
                reactQueryKeys: QUERY_KEYS.SERVICES,
                onSuccess: () => {
                    refetchServices()
                },
            }
        )

    const { mutateAsync: registerServiceMutation, isPending: isRegistering } =
        useMutationWithHandler(
            { mutationFn: registerService },
            {
                successMessage: t('register-service-success'),
                reactQueryKeys: QUERY_KEY_COMBINATIONS.SERVICE_WITH_TICKET,
                onSuccess: () => {
                    refetch()
                    refetchServices()
                },
            }
        )

    const formControl = useForm<{ name: string }>({
        defaultValues: {
            name: service?.name || '',
        },
    })

    const onSubmit = async (values: { name: string }) => {
        try {
            if (service?.name && service?.name === values?.name) {
                // Service already has name, just register
                await registerServiceMutation(service?.service_id)
            } else {
                // Step 1: Update entity name first
                await updateEntityName({
                    serviceID: service?.service_id,
                    name: values?.name,
                })
                // Step 2: Then register service
                await registerServiceMutation(service?.service_id)
            }
        } catch (error) {
            console.error('Registration flow failed:', error)
        }
    }

    const isPending = isUpdating || isRegistering

    return (
        <GridHookForm
            className="mb-4"
            formControl={formControl}
            data={[
                {
                    formItemProps: {
                        name: 'name',
                        type: 'textfield',
                        helperText: t('helper-text-entity-name'),
                        label: t('entity-name'),
                        placeholder: t('entity-name'),
                    },
                },
            ]}
            actions={
                service?.name
                    ? [
                          {
                              title: t('register-service'),
                              type: 'submit',
                              disabled: isPending,
                              loading: isPending,
                              variant: 'contained',
                          },
                      ]
                    : [
                          {
                              title: isUpdating
                                  ? t('updating-entity-name')
                                  : isRegistering
                                    ? t('registering-service')
                                    : t('setup-and-register'),
                              type: 'submit',
                              disabled:
                                  !formControl.formState?.isDirty || isPending,
                              loading: isPending,
                              variant: 'contained',
                          },
                      ]
            }
            onSubmit={onSubmit}
        />
    )
}

export default SwiftFederationRegisterForm
